import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button } from '@mui/material'
import React, { useCallback, useEffect } from 'react'
import LinkVariant from 'src/Elements/Buttons/LinkVariant'
import { SeparatorLineWithText } from 'src/Elements/Separator'
import { ExtensionConnectionTypes, ExtensionState } from 'src/Libs/installation-extensions/types'
import { makeStyles } from 'tss-react/mui'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { useIsWebviewMode } from 'src/Hooks'
import { sendMessageToReactNative } from '@e3dc-react/shell/Libs/react-native'
import { useExtensionConnection } from 'src/Libs/installation-extensions/hooks/extensions'
import { useTypedSelector } from 'src/State/RootReducer'
import useInstallationExtensionData from 'src/Libs/installation-extensions/hooks/extensions/use-installation-extension-data'
import useModals from 'src/Hooks/useModals'
import { DisconnectButton } from '../../general'

const useStyles = makeStyles<{ hasConnectionUrlError: boolean; isDisabled?: boolean }>()((theme, props) => ({
  button: {
    borderRadius: theme.spacing(12),
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  disabledButton: {
    backgroundColor: theme.palette.grey.grey06,
    borderColor: theme.palette.grey.grey06,
    pointerEvents: 'none',
    textDecoration: 'none',
  },
  link: {
    padding: theme.spacing(1),
    cursor: 'pointer',
  },
  connectionLink: {
    color: props.hasConnectionUrlError ? 'inherit' : props.isDisabled ? theme.palette.grey.grey03 : 'inherit',
    cursor: 'pointer',
  },
}))

const REDIRECT_URL_BASE = 'https://join.ostrom.de/tariff-plan?usage=1400&referralCode=E3dcostrom&language='
const REDIRECT_URL_LANG_DE = 'de_DE'
const REDIRECT_URL_LANG_EN = 'en'

interface FooterContentProps {
  extensionState?: ExtensionState
  hasActiveTariff?: boolean
}

/**
 * Ostroms detail view's footer content. Allows to get an offer or to link an existing account.
 */
const FooterContent: React.FC<FooterContentProps> = ({ extensionState, hasActiveTariff = false }) => {
  const { t, selectedAppLanguage } = useCustomTranslation()
  const {
    createExtensionConnectionUrl: createOstromConnectionUrl,
    extensionConnectionUrl: ostromConnectionUrl,
    hasConnectionUrlError,
  } = useExtensionConnection(ExtensionConnectionTypes.Ostrom)
  const { openOStromDeleteConnectionModal } = useModals()
  const isWebview = useIsWebviewMode()
  const installationId = useTypedSelector(state => state.storage.activeInstallation)?.toString() ?? ''
  const { isLoading, loadInstallationExtensionStates } = useInstallationExtensionData(installationId)
  const disabledButtonTooltip = `installation-extensions.ostrom.connection-button.disabled-tooltip`
  const connectionUrlErrorTooltip = `installation-extensions.o-strom.connection-creation.error`
  const { classes, cx } = useStyles({ hasConnectionUrlError, isDisabled: hasActiveTariff })

  const REDIRECT_URL = selectedAppLanguage === 'de' ? `${REDIRECT_URL_BASE}${REDIRECT_URL_LANG_DE}` : `${REDIRECT_URL_BASE}${REDIRECT_URL_LANG_EN}`

  useEffect(() => {
    if (!ostromConnectionUrl) createOstromConnectionUrl()
  }, [ostromConnectionUrl]) //eslint-disable-line

  const handleOnClick = useCallback((): void => {
    if (ostromConnectionUrl) {
      sendMessageToReactNative({ type: 'ostrom-url', url: ostromConnectionUrl })
    }
  }, [ostromConnectionUrl])

  const handleDisconnectExtension = (): void => {
    openOStromDeleteConnectionModal({ loadInstallationExtensionStates })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
      {!extensionState && !isLoading && (
        <>
          <OptionalTooltipWrapper
            hasTooltip={hasActiveTariff || hasConnectionUrlError}
            titleKey={hasActiveTariff ? disabledButtonTooltip : connectionUrlErrorTooltip}
          >
            <Button
              className={hasActiveTariff ? classes.disabledButton : classes.button}
              onClick={isWebview ? handleOnClick : undefined}
              disabled={hasConnectionUrlError}
            >
              <LinkVariant
                type="link"
                link={!isWebview ? ostromConnectionUrl ?? '' : undefined}
                hasUnderline={false}
                textClassName={cx(classes.link, classes.connectionLink)}
                label={t('installation-extensions.o-strom.details.link-text')}
                clickable={!hasConnectionUrlError}
              />
            </Button>
          </OptionalTooltipWrapper>
          <SeparatorLineWithText text={t('installation-extensions.o-strom.details.separator-text')} />
          <LinkVariant
            type="link"
            hasUnderline={false}
            label={t('installation-extensions.o-strom.details.button-text')}
            textClassName={cx(classes.link)}
            link={REDIRECT_URL}
          />
        </>
      )}
      <DisconnectButton isConnected={extensionState === 'active'} onClickLink={handleDisconnectExtension} />
    </Box>
  )
}

export default FooterContent
